import { Children, type ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';

import { cn } from '@/lib/utils';

/** Slash-separated navigation trail.
 *
 * Pass the trail as children — typically a mix of `<Link>` (for clickable
 * ancestors) and `<span>` (for the current page, which shouldn't link to
 * itself). Separators are inserted automatically between siblings.
 *
 * Example:
 *
 *   <Breadcrumbs>
 *     <Link to="/customers">Customers</Link>
 *     <Link to="/customers/$slug" params={{ slug }}>{customerName}</Link>
 *     <span>{engagementName}</span>
 *   </Breadcrumbs>
 */
export function Breadcrumbs({ children, className }: { children: ReactNode; className?: string }) {
  const items = Children.toArray(children).filter(Boolean);
  return (
    <nav
      aria-label="Breadcrumb"
      className={cn('flex flex-wrap items-center gap-1.5 text-sm text-brand-slate', className)}
    >
      {items.map((child, i) => (
        <span key={i} className="flex items-center gap-1.5">
          {i > 0 && (
            <ChevronRight size={14} strokeWidth={1.75} className="text-brand-slate/60" />
          )}
          {child}
        </span>
      ))}
    </nav>
  );
}
